const EASY_GAMES_CART_RENDERED_EVENT = 'easygames:cart-rendered';

const getEasyGamesDigitalConfigSection = () => {
  const config = document.querySelector('[data-eg-digital-config][data-section-id]');
  const sectionId = config?.dataset.sectionId;
  if (!sectionId) return null;

  const wrapperId = `shopify-section-${sectionId}`;
  if (!document.getElementById(wrapperId)) return null;

  return {
    sectionId,
    wrapperId,
    selector: '.eg-digital-config-host',
  };
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.cartDrawerCloseBound === 'true') return;
    overlay.dataset.cartDrawerCloseBound = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    const updates = this.getSectionsToRender()
      .map((section) => this.createSectionUpdate(section, parsedState.sections?.[section.id]))
      .filter(Boolean);

    this.applySectionUpdates(updates);
    this.completeRender(parsedState);
  }

  async refreshAfterCartMutation(cartData, productId) {
    if (!cartData || !Array.isArray(cartData.items)) {
      throw new Error('Cart drawer refresh requires the authoritative cart state');
    }

    const sections = this.getSectionsToRender();
    const results = await Promise.allSettled(
      sections.map(async (section) => ({
        section,
        html: await this.fetchSectionMarkup(section),
      })),
    );

    const drawerResultIndex = sections.findIndex((section) => section.id === 'cart-drawer');
    const drawerResult = results[drawerResultIndex];
    if (!drawerResult || drawerResult.status !== 'fulfilled') {
      throw drawerResult?.reason || new Error('Cart drawer section refresh failed');
    }

    const drawerUpdate = this.createSectionUpdate(drawerResult.value.section, drawerResult.value.html);
    if (!drawerUpdate) throw new Error('Cart drawer section response was incomplete');

    const updates = [drawerUpdate];
    results.forEach((result, index) => {
      if (index === drawerResultIndex || result.status !== 'fulfilled') return;
      const update = this.createSectionUpdate(result.value.section, result.value.html, true);
      if (update) updates.push(update);
    });

    this.productId = productId;
    this.applySectionUpdates(updates);

    const parsedState = {
      ...cartData,
      id: parseInt(productId, 10) || productId,
    };
    this.completeRender(parsedState);
    return parsedState;
  }

  async fetchSectionMarkup(section) {
    const url = section.id === 'cart-drawer'
      ? new URL(routes.cart_url, window.location.origin)
      : new URL(window.location.href);

    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    url.searchParams.set('section_id', section.id);

    const response = await fetch(`${url.pathname}${url.search}`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart section refresh failed for ${section.id} (${response.status})`);
    }

    return response.text();
  }

  createSectionUpdate(section, renderedHtml, allowBodyFallback = false) {
    if (!renderedHtml) return null;

    const target = section.selector
      ? document.querySelector(section.selector)
      : document.getElementById(section.id);
    if (!target) return null;

    const innerHTML = this.getSectionInnerHTML(renderedHtml, section.selector, allowBodyFallback);
    if (innerHTML === null) return null;

    return { target, innerHTML };
  }

  applySectionUpdates(updates) {
    updates.forEach(({ target, innerHTML }) => {
      target.innerHTML = innerHTML;
    });
  }

  completeRender(parsedState) {
    if (typeof parsedState?.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    } else {
      this.classList.remove('is-empty');
    }

    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.bindOverlayClose();

    document.dispatchEvent(
      new CustomEvent(EASY_GAMES_CART_RENDERED_EVENT, {
        detail: { source: 'cart-drawer' },
      }),
    );

    // Publish only after the drawer and EASY GAMES digital metadata are in their
    // final server-rendered DOM. CartDrawerItems ignores source=cart-items, so
    // this does not start a redundant refresh race.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
    }

    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section', allowBodyFallback = false) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedDocument.querySelector(selector);
    if (source) return source.innerHTML;

    if (!allowBodyFallback || selector !== '.shopify-section') return null;
    const fallback = parsedDocument.body?.innerHTML?.trim();
    return fallback || null;
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];

    const digitalConfig = getEasyGamesDigitalConfigSection();
    if (digitalConfig) {
      sections.push({
        id: digitalConfig.sectionId,
        selector: digitalConfig.selector,
      });
    }

    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();

    // ProductForm and CartDrawer already own their respective post-add renders.
    // Avoid a second drawer fetch racing those renders; all other external cart
    // updates remain supported by CartItems.onCartUpdate().
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'product-form') return;
      return this.onCartUpdate();
    });
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];

    const digitalConfig = getEasyGamesDigitalConfigSection();
    if (digitalConfig) {
      sections.push({
        id: digitalConfig.wrapperId,
        section: digitalConfig.sectionId,
        selector: digitalConfig.selector,
      });
    }

    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
